import React from 'react';
import { Link } from 'react-router-dom';
import { useFormState, useWatch } from 'react-hook-form';
import {
    DateInput,
    Form,
    TextInput,
    TranslatableInputs,
    useCreatePath,
    useFormGroup,
    useRecordContext,
    useSaveContext,
    useTranslatableContext,
} from 'react-admin';
import { Alert, Box, Button, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import {
    LOCALE_NAMES,
    NEWS_LOCALES,
    NEWS_POSTS_RESOURCE,
    SOURCE_LOCALE,
    TITLE_MAX_LENGTH,
    isLongAutoTranslation,
    textNotification,
    validateNewsPost,
} from '../common';
import { TranslationStatusDot, TranslationStatusLegend } from '../chips';
import { LoadingLabel, filledButtonSx, outlinedButtonSx } from '../buttons';
import NewsPopupPreview, { PREVIEW_MAX_WIDTH } from '../preview/NewsPopupPreview';
import RichBodyInput from './RichBodyInput';
import ImagePicker from './ImagePicker';
import { FieldLabel, HelpText, smallTextSx } from './CharCounter';

export const TITLE_PLACEHOLDER = 'Ej.: Nuevo informe de embudo por oferta';

// Inputs con borde y la etiqueta encima, fuera del input, como en el prototipo.
const outlinedInputSx = { mt: 0, mb: 0 };

const TitleInput = ({ source, label }) => {
    const value = useWatch({ name: source }) || '';

    return (
        <Box>
            <FieldLabel htmlFor={source} label={label} count={value.length} max={TITLE_MAX_LENGTH} />
            <TextInput
                source={source}
                label={false}
                variant="outlined"
                fullWidth
                helperText={false}
                placeholder={TITLE_PLACEHOLDER}
                inputProps={{ maxLength: TITLE_MAX_LENGTH, 'aria-label': label }}
                sx={outlinedInputSx}
            />
        </Box>
    );
};

const LocaleTab = ({ locale, status, warning, ...rest }) => {
    const { isValid, isTouched } = useFormGroup(locale);
    const { isSubmitted } = useFormState();
    // Tras enviar también: un error del back puede caer en un idioma que no se ha tocado.
    const hasError = !isValid && (isTouched || isSubmitted);

    return (
        <Tab
            {...rest}
            id={`translatable-header-${locale}`}
            sx={{ minHeight: 48, textTransform: 'none', ...(hasError && { color: 'error.main' }) }}
            label={
                <Stack direction="row" alignItems="center" spacing={0.75}>
                    <span>
                        {LOCALE_NAMES[locale]}
                        {locale === SOURCE_LOCALE && ' *'}
                    </span>
                    {locale !== SOURCE_LOCALE && status && <TranslationStatusDot status={status} warning={warning} />}
                </Stack>
            }
        />
    );
};

/**
 * Pestañas de idioma con el estado de cada traducción. Sustituye al selector por defecto de
 * `TranslatableInputs` para pintar el punto de estado «Automática / Corregida a mano / Sin traducir»
 * (con su leyenda debajo) y
 * para avisar del idioma abierto a la vista previa, que vive fuera de `TranslatableInputs`.
 */
const LocaleTabs = ({ statuses, onLocaleChange }) => {
    const { locales, selectedLocale, selectLocale } = useTranslatableContext();
    const record = useRecordContext();
    // Se recalcula al escribir: si alguien acorta la traducción, el punto deja de estar en naranja.
    const [title, body] = useWatch({ name: ['title', 'body'] });
    const longAuto = locales.filter((locale) => isLongAutoTranslation(record, { title, body }, locale));

    React.useEffect(() => {
        onLocaleChange(selectedLocale);
    }, [selectedLocale, onLocaleChange]);

    return (
        <Box sx={{ mb: 2 }}>
            <Tabs
                value={selectedLocale}
                onChange={(event, locale) => selectLocale(locale)}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="Idiomas"
                sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
                {locales.map((locale) => (
                    <LocaleTab key={locale} value={locale} locale={locale} status={statuses?.[locale]} warning={longAuto.includes(locale)} />
                ))}
            </Tabs>
            {statuses && (
                <Box sx={{ pt: 1 }}>
                    <TranslationStatusLegend statuses={statuses} withWarning={longAuto.length > 0} />
                </Box>
            )}
        </Box>
    );
};

/** Subetiqueta encima del input, como «Desde» y «Hasta (incluido)» en el prototipo. */
const SubLabel = ({ htmlFor, children }) => (
    <Typography component="label" htmlFor={htmlFor} color="text.secondary" sx={{ ...smallTextSx, display: 'block', mb: 0.5 }}>
        {children}
    </Typography>
);

/** «Vigencia del pop-up» con un solo mensaje de error para las dos fechas, como el prototipo. */
const ValidityInputs = () => {
    const { errors, isSubmitted } = useFormState({ name: ['starts_at', 'ends_at'] });
    // Los errores de la validación del formulario llegan como `{ message: { message } }` (formato de
    // react-admin) y los del back como texto plano.
    const text = (error) => (typeof error?.message === 'string' ? error.message : error?.message?.message);
    const message = isSubmitted ? text(errors.starts_at) || text(errors.ends_at) : null;

    return (
        <Box sx={{ mt: 1 }}>
            <FieldLabel label="Vigencia del pop-up" />
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    // El error va debajo del bloque, una sola vez: se oculta el de cada input.
                    '& .MuiFormHelperText-root': { display: 'none' },
                }}
            >
                {/* `label={false}` y etiqueta propia encima: el id del input es su `source`. DateInput pasa
                    siempre un `label` (vacío) al TextField, que deja el hueco de la etiqueta en el borde;
                    `notched: false` lo cierra. */}
                <Box sx={{ flex: '1 1 180px' }}>
                    <SubLabel htmlFor="starts_at">Desde</SubLabel>
                    <DateInput source="starts_at" label={false} variant="outlined" fullWidth helperText={false} InputProps={{ notched: false }} sx={outlinedInputSx} />
                </Box>
                <Box sx={{ flex: '1 1 180px' }}>
                    <SubLabel htmlFor="ends_at">Hasta (incluido)</SubLabel>
                    <DateInput source="ends_at" label={false} variant="outlined" fullWidth helperText={false} InputProps={{ notched: false }} sx={outlinedInputSx} />
                </Box>
            </Box>
            {message && (
                <HelpText color="error" role="alert">
                    {message}
                </HelpText>
            )}
        </Box>
    );
};

/**
 * Aviso informativo con el aspecto del `Alert severity="info"` del ATS: colores `info` de MUI (ni
 * freezer ni cooler los cambian en su tema) y los overrides de `GlobalStyles.jsx` de freezer
 * (radio 12, icono arriba, mensaje sin padding) con su `body2` (12 px, interlineado 1.5).
 */
const InfoBox = ({ children }) => (
    <Alert
        severity="info"
        sx={{
            borderRadius: '12px',
            alignItems: 'center',
            fontSize: '12px',
            lineHeight: 1.5,
            '& .MuiAlert-icon': { alignSelf: 'flex-start' },
            '& .MuiAlert-message': { p: 0 },
        }}
    >
        {children}
    </Alert>
);

const FormPreview = ({ locale }) => {
    const [title, body, image, startsAt, endsAt] = useWatch({ name: ['title', 'body', 'image', 'starts_at', 'ends_at'] });

    return (
        <NewsPopupPreview
            title={title}
            body={body}
            imageSrc={image?.src || null}
            startsAt={startsAt}
            endsAt={endsAt}
            locale={locale}
        />
    );
};

/**
 * Cancelar y Publicar / Guardar cambios. Mientras se guarda (el `isSubmitting` del formulario, que
 * dura hasta que react-admin resuelve el `save`, o el `saving` del SaveContext), los dos quedan
 * deshabilitados y el principal muestra un spinner sin cambiar de ancho: no hay doble envío.
 * Sustituye a `SaveButton`, que mete el spinner como icono y ensancha el botón.
 */
const FormToolbar = ({ isCreate }) => {
    const createPath = useCreatePath();
    const { saving } = useSaveContext();
    const { isSubmitting, isValidating, dirtyFields } = useFormState();
    const busy = Boolean(saving || isSubmitting);
    const label = isCreate ? 'Publicar' : 'Guardar cambios';

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 1.5 }}>
            <Button
                component={Link}
                to={createPath({ resource: NEWS_POSTS_RESOURCE, type: 'list' })}
                variant="outlined"
                color="inherit"
                disabled={busy}
                sx={outlinedButtonSx}
            >
                Cancelar
            </Button>
            <Button
                type="submit"
                variant="contained"
                aria-label={label}
                aria-busy={busy}
                disabled={busy || isValidating || Object.keys(dirtyFields).length === 0}
                sx={filledButtonSx}
            >
                <LoadingLabel loading={busy}>{label}</LoadingLabel>
            </Button>
        </Box>
    );
};

// La tarjeta de react-admin lleva `overflow: hidden`, que anula el `position: sticky` de la vista previa.
export const stickyPreviewSx = { '& .RaCreate-card, & .RaEdit-card': { overflow: 'visible' } };

/**
 * Aviso de error al guardar. Con un 400 de validación, el dataProvider ya ha dejado en `message`
 * la lista de errores del back, una por línea: `multiLine` respeta los saltos.
 */
export const notifySaveError = (notify) => (error) =>
    error?.message
        ? notify(...textNotification(error.message, { type: 'error', multiLine: true }))
        : notify('ra.notification.http_error', { type: 'error' });

/** Formulario único de alta y edición de una novedad, maquetado como el prototipo validado. */
const NewsPostForm = ({ isCreate = false }) => {
    const record = useRecordContext();
    const createPath = useCreatePath();
    const [locale, setLocale] = React.useState(SOURCE_LOCALE);
    const validate = React.useMemo(() => validateNewsPost(isCreate ? undefined : record), [isCreate, record]);

    return (
        <Form validate={validate}>
            {/* Contenedor centrado de 1440 px como máximo y reparto en torno a 60/40 (crecimiento 3:2).
                La columna de la vista previa mide como mucho lo mismo que la vista previa (el ancho del
                modal del ATS), así que no queda hueco muerto: lo que sobra se lo lleva el formulario.
                En pantallas estrechas, la vista previa pasa debajo. */}
            <Box sx={{ p: { xs: 2, md: 4 }, display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 1440, mx: 'auto' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Button
                        component={Link}
                        to={createPath({ resource: NEWS_POSTS_RESOURCE, type: 'list' })}
                        size="small"
                        sx={{ alignSelf: 'flex-start', px: 0, minWidth: 0, fontWeight: 400 }}
                    >
                        ← Volver al listado
                    </Button>
                    <Typography variant="h1" sx={{ fontSize: 26, fontWeight: 600, m: 0 }}>
                        {isCreate ? 'Nueva publicación' : 'Editar publicación'}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-start' }}>
                    <Paper
                        variant="outlined"
                        sx={{ flex: '3 1 520px', minWidth: 0, p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}
                    >
                        <Box>
                            <Typography color="text.secondary" sx={{ fontSize: '13px', lineHeight: 1.5, mb: 1 }}>
                                {isCreate
                                    ? 'Escribe en español: al publicar se traduce automáticamente al resto de idiomas. Si rellenas otro idioma, se respeta tu versión.'
                                    : 'Si cambias el español, se vuelven a traducir los idiomas automáticos; los corregidos a mano se respetan. Vacía un idioma para que vuelva a traducirse.'}
                            </Typography>
                            <TranslatableInputs
                                locales={NEWS_LOCALES}
                                defaultLocale={SOURCE_LOCALE}
                                selector={<LocaleTabs statuses={record?.translation_status} onLocaleChange={setLocale} />}
                                fullWidth
                                sx={{ m: 0 }}
                                // Sin el recuadro interior de react-admin (bordes izquierdo, derecho e
                                // inferior, pensado para colgar de sus pestañas): la tarjeta ya enmarca el
                                // formulario y con nuestras pestañas quedaba un recuadro sin borde de arriba.
                                // Mismo selector que usa react-admin (`&.RaTranslatableInputsTabContent-root`,
                                // dos clases): con uno más débil, su borde gana aunque vaya después.
                                StackProps={{
                                    sx: {
                                        '&.RaTranslatableInputsTabContent-root': { border: 0, borderRadius: 0, p: 0, gap: 2.5 },
                                    },
                                }}
                            >
                                <TitleInput source="title" label="Título" />
                                <RichBodyInput source="body" label="Texto" />
                            </TranslatableInputs>
                        </Box>
                        <ImagePicker source="image" />
                        <ValidityInputs />
                        <InfoBox>
                            Durante la vigencia, cada usuario verá este pop-up como máximo 3 veces (una por acceso). El
                            pop-up incluye un enlace a Novedades, donde la publicación se queda para siempre.
                        </InfoBox>
                        <FormToolbar isCreate={isCreate} />
                    </Paper>
                    <Box
                        sx={{
                            flex: '2 1 380px',
                            width: '100%',
                            maxWidth: PREVIEW_MAX_WIDTH,
                            minWidth: 0,
                            position: { md: 'sticky' },
                            top: { md: 72 },
                        }}
                    >
                        <FormPreview locale={locale} />
                    </Box>
                </Box>
            </Box>
        </Form>
    );
};

export default NewsPostForm;
